import { CardElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { StripeCardElement } from "@stripe/stripe-js";
import { useContext, useState } from "react";
import { getCardType } from "./stripeActions";
import { confirmPaymentIntent, successPaymentStatus } from "./paymentIntent";
import PaymentDetails from "./PaymentDetails";
import { CardType, defaultBillingDetails, getErrorMessage, IBillingDetails, IProps } from "./utils";
import { Context } from "../../context/ContextApp";

const STRIPE_UNAVAILABLE_ERROR =
  "We're having trouble processing your payment right now. Please try again shortly.";

const isValidEmail = (email: string) => /\S+@\S+\.\S+/.test(email);

const StripeForm = (props: IProps) => {
  const { appBranch } = useContext(Context);
  const { paymentDetailsOptions, orderId } = props;
  const [processing, setProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [cardType, setCardType] = useState<CardType | null>(null);
  const [billingDetails, setBillingDetails] = useState<IBillingDetails>(defaultBillingDetails);
  const stripe = useStripe();
  const elements = useElements();

  // Resolve payment details based on detected card type (national vs international)
  const paymentDetails = cardType && paymentDetailsOptions && paymentDetailsOptions[cardType];
  const hasCardholderName = billingDetails.cardholderName !== "";
  const hasValidEmail = billingDetails.email !== "" && isValidEmail(billingDetails.email);
  const hasValidFields = hasCardholderName && hasValidEmail && cardType !== null;

  const renderButtonText = () => {
    if (processing) return "Processing...";
    if (paymentDetails) return `Pay NZD $${paymentDetails.grand_total}`;
    return "Pay";
  };

  const handleError = (message: string) => {
    setErrorMessage(message);
    setProcessing(false);
  };

  // Detect card type on every card change to display the correct pricing
  const handleCardChange = async () => {
    setErrorMessage(null);
    const cardTypeResponse = await getCardType(stripe, elements, CardElement, appBranch);
    // error case
    if (cardTypeResponse?.status === "error" && cardTypeResponse?.errorCode) {
      const errorMessage = getErrorMessage(cardTypeResponse.errorCode);
      handleError(errorMessage);
      setCardType(null);
    }
    // success case
    if (cardTypeResponse?.cardType) setCardType(cardTypeResponse?.cardType);
  };

  // Handles 3DS authentication and re-confirms the payment intent after the action is completed
  const handle3DSAction = async (clientSecret: string, paymentMethodId: string) => {
    if (!stripe) return;
    const { error, paymentIntent } = await stripe.handleNextAction({ clientSecret });
    if (error) {
      handleError(error.message ?? "Authentication failed. Please try again.");
      return;
    }
    if (paymentIntent?.status === "succeeded" || paymentIntent?.status === "processing") {
      // Re-confirm to record the payment in our database after 3DS completes
      const confirmResponse = await confirmPaymentIntent(orderId, paymentMethodId);
      if (confirmResponse.status !== 200) {
        handleError(getErrorMessage(confirmResponse.message));
        return;
      }
      window.location.reload();
    } else {
      handleError("Authentication was not completed. Please try again.");
    }
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setErrorMessage(null);
    setProcessing(true);

    if (!hasValidFields) {
      handleError("Please fill in all fields.");
      return;
    }

    // Guard: Stripe SDK must be loaded before processing
    if (!stripe || !elements) {
      handleError(STRIPE_UNAVAILABLE_ERROR);
      return;
    }

    const cardElement = elements.getElement(CardElement);
    if (!cardElement) {
      handleError(STRIPE_UNAVAILABLE_ERROR);
      return;
    }

    // Create a payment method from the card element
    const { error: paymentMethodError, paymentMethod } = await stripe.createPaymentMethod({
      card: cardElement as StripeCardElement,
      type: "card",
      billing_details: {
        name: billingDetails.cardholderName,
        email: billingDetails.email,
      },
    });

    if (paymentMethodError || !paymentMethod) {
      handleError("Having trouble with the Stripe API, please try again later.");
      return;
    }

    // Confirm the payment intent with our backend
    const response = await confirmPaymentIntent(orderId, paymentMethod.id);

    if (response.status !== 200) {
      handleError(getErrorMessage(response.message));
      return;
    }

    setProcessing(false);

    // 3DS required: trigger authentication flow before reloading
    if (response.message === successPaymentStatus.payment_requires_action) {
      await handle3DSAction(response.clientSecret, paymentMethod.id);
      return;
    }

    if (
      [successPaymentStatus.payment_confirmed, successPaymentStatus.payment_processing].includes(
        response.message as successPaymentStatus,
      )
    ) {
      window.location.reload();
    }
  };

  return (
    <div className="stripeForm">
      {/* LEFT COLUMN: Product Information */}
      <div className="productInfo">
        <h2 className="productInfo_title">Fashionbiz Checkout</h2>
        <PaymentDetails options={paymentDetailsOptions} paymentDetails={paymentDetails} />
      </div>

      {/* RIGHT COLUMN: Payment Form */}
      <div className="formWrapper">
        <form onSubmit={handleSubmit} className="form">
          <h3 className="heading">Enter Payment Details</h3>
          <div className="cardElementContainer">
            <CardElement
              onChange={handleCardChange}
              className="cardElement"
              options={{
                hidePostalCode: true,
              }}
            />
          </div>
          <label className="label">
            Cardholder Name
            <input
              type="text"
              value={billingDetails.cardholderName}
              onChange={(e) =>
                setBillingDetails({
                  ...billingDetails,
                  cardholderName: e.target.value,
                })
              }
              className="input"
              required
            />
          </label>
          <label className="label">
            Email
            <input
              type="email"
              pattern="[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$"
              value={billingDetails.email}
              onChange={(e) => setBillingDetails({ ...billingDetails, email: e.target.value })}
              className="input"
              required
            />
          </label>

          {errorMessage && <div className="error">{errorMessage}</div>}
          <button
            type="submit"
            disabled={!stripe || processing || !hasValidFields}
            className="payButton"
          >
            {renderButtonText()}
          </button>
          {cardType && paymentDetails && (
            <p className="description">You will be charged NZD ${paymentDetails.grand_total}.</p>
          )}
        </form>
      </div>
    </div>
  );
};

export default StripeForm;
